import React, { useEffect, useState } from 'react';
import { Bus, Train, X, Loader2 } from 'lucide-react';
import { useTranslation } from '../../useTranslation';
import { resolveGroundTripRoute } from '../../services/api/groundTripsApi';
import type { GroundTrip } from '../../types/ground';
import { THEMES, softBreakAfterHyphens, parseGroundStops, minutesToFormatted } from './groundTripUtils';
import { TagBadge } from '../common/TagBadge';
import {
  airportNameLabel,
  cityCountryLabel,
} from '../../utils/airportLabels';
import {
  addDaysIso,
  arrivalDayOffset,
} from '../../utils/flightDayOffset';
import { localizeTrainType } from '../../utils/trainTypeLabel';

export const ViewGroundTripModal: React.FC<{
  open: boolean;
  onClose: () => void;
  trip: GroundTrip | null;
  language?: string;
  onTripPatched?: (trip: GroundTrip) => void;
}> = ({ open, onClose, trip, language, onTripPatched }) => {
  const lang =
    language ||
    (localStorage.getItem('travelDiarySettings')
      ? JSON.parse(localStorage.getItem('travelDiarySettings')!).language
      : 'ru-RU');
  const { t, formatDate, formatNumber, formatDistance } = useTranslation(lang);
  const settings = localStorage.getItem('travelDiarySettings')
    ? JSON.parse(localStorage.getItem('travelDiarySettings')!)
    : { distanceUnit: 'km', currency: '₽' };

  const [llmStops, setLlmStops] = useState<string[]>([]);
  const [trainName, setTrainName] = useState<string | null>(null);
  const [trainNameEn, setTrainNameEn] = useState<string | null>(null);
  const [stopsLoading, setStopsLoading] = useState(false);
  const [nameLoading, setNameLoading] = useState(false);
  const [routeReady, setRouteReady] = useState(false);
  const onTripPatchedRef = React.useRef(onTripPatched);
  onTripPatchedRef.current = onTripPatched;

  useEffect(() => {
    if (!open || !trip || trip.mode !== 'train') {
      setLlmStops([]);
      setTrainName(null);
      setTrainNameEn(null);
      setStopsLoading(false);
      setNameLoading(false);
      setRouteReady(false);
      return;
    }

    const tripId = trip.id;
    const cachedStops = trip.routeStops;
    const nameField = trip.trainName;
    const nameResolved = nameField != null;
    const useRuLang = lang.toLowerCase().startsWith('ru');
    // One-shot: cached Chinese glyphs while UI is Russian → refresh labels, then keep cache
    const hasCjk = (s: string) => /[\u4e00-\u9fff]/.test(s);
    const forceLangFix =
      useRuLang &&
      Array.isArray(cachedStops) &&
      cachedStops.some((s) => hasCjk(String(s)));

    if (cachedStops != null && !forceLangFix) {
      setLlmStops(cachedStops);
      setRouteReady(true);
    } else {
      setLlmStops(cachedStops ?? []);
      setRouteReady(false);
    }
    setTrainName(nameField?.trim() || null);
    setTrainNameEn(trip.trainNameEn?.trim() || null);

    if (cachedStops != null && nameResolved && !forceLangFix) {
      setStopsLoading(false);
      setNameLoading(false);
      return;
    }

    let cancelled = false;
    setStopsLoading(cachedStops == null || forceLangFix);
    setNameLoading(!nameResolved);
    void resolveGroundTripRoute(tripId, { lang, force: forceLangFix })
      .then(res => {
        if (cancelled) return;
        const stops = Array.isArray(res.stops) ? res.stops : cachedStops ?? [];
        const rawName = res.trainName == null ? '' : String(res.trainName);
        const rawEn = res.trainNameEn == null ? '' : String(res.trainNameEn);
        setLlmStops(stops);
        setTrainName(rawName.trim() || null);
        setTrainNameEn(rawEn.trim() || null);
        setRouteReady(true);
        onTripPatchedRef.current?.({
          ...trip,
          routeStops: stops,
          trainName: rawName,
          trainNameEn: rawEn || trip.trainNameEn,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setRouteReady(true);
          if (cachedStops != null) {
            onTripPatchedRef.current?.({
              ...trip,
              routeStops: cachedStops,
              trainName: trip.trainName ?? '',
            });
          }
        }
      })
      .finally(() => {
        if (!cancelled) {
          setStopsLoading(false);
          setNameLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // Intentionally omit routeStops/trainName: patching them must not re-trigger resolve
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    trip?.id,
    trip?.mode,
    trip?.routeNumber,
    trip?.originCity,
    trip?.destinationCity,
    trip?.date,
    lang,
  ]);

  if (!open || !trip) return null;

  const isTrain = trip.mode === 'train';
  const theme = THEMES[trip.mode];
  const Icon = isTrain ? Train : Bus;
  const durationLabel = trip.formattedDuration || minutesToFormatted(trip.duration);
  const carrier = trip.operator?.trim() || '';
  const trainNumber = trip.routeNumber?.trim() || '';
  const useRu = lang.toLowerCase().startsWith('ru');
  const typeLabel = localizeTrainType(trainName || trip.trainName, trainNameEn || trip.trainNameEn, useRu);
  const stops = isTrain ? llmStops : parseGroundStops(trip.notes);
  const originLabel =
    airportNameLabel(trip.origin_station_info, trip.originCity, useRu) ||
    cityCountryLabel(trip.origin_station_info, trip.originCity, useRu) ||
    trip.originCity;
  const destLabel =
    airportNameLabel(trip.destination_station_info, trip.destinationCity, useRu) ||
    cityCountryLabel(trip.destination_station_info, trip.destinationCity, useRu) ||
    trip.destinationCity;
  const timeline = [originLabel, ...stops, destLabel].filter(Boolean);
  const dayOffset = isTrain
    ? arrivalDayOffset({
        departureTime: trip.departure_time,
        arrivalTime: trip.arrival_time,
        flightDate: trip.date,
      })
    : 0;
  const depTime = (trip.departure_time || '').trim();
  const arrTime = (trip.arrival_time || '').trim();
  const arrivalDateIso = dayOffset > 0 ? addDaysIso(trip.date, dayOffset) : null;
  const dateLabel = arrivalDateIso
    ? `${formatDate(trip.date)} → ${formatDate(arrivalDateIso)}`
    : formatDate(trip.date);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-stone-900/50 dark:bg-black/60 backdrop-blur-sm p-0 sm:p-4" role="dialog" aria-modal="true">
      <div className="relative w-full sm:max-w-lg max-h-[100dvh] sm:max-h-[min(90dvh,720px)] flex flex-col rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl border border-indigo-200/50 dark:border-slate-600 bg-gradient-to-b from-indigo-50/90 via-white to-stone-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-950">
        <div className="relative shrink-0 px-4 sm:px-5 pt-4 pb-3 border-b border-indigo-100 dark:border-slate-700/80">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(99,102,241,0.18),_transparent_55%)] pointer-events-none" />
          <div className="relative flex items-start gap-3 pr-10">
            <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white shadow-md ${theme.iconBg}`}>
              <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              {trip.tag ? (
                <h2 className="min-w-0">
                  <TagBadge tag={trip.tag} className="text-sm" />
                </h2>
              ) : (
                <p className="text-[11px] uppercase tracking-wider text-indigo-600/80 dark:text-indigo-300/80 font-semibold">
                  {isTrain ? t.trainDetails : t.tripDetails}
                </p>
              )}
              <p className="text-xs text-stone-500 dark:text-slate-400 mt-1 tabular-nums">
                {softBreakAfterHyphens(originLabel)}
                {" \u200B→ "}
                {softBreakAfterHyphens(destLabel)}
                {" · "}
                {dateLabel}
              </p>
              {isTrain && (depTime || arrTime) ? (
                <p className="text-xs text-stone-500 dark:text-slate-400 mt-0.5 tabular-nums">
                  {depTime || '—'}
                  {arrTime ? (
                    <>
                      {' – '}
                      {arrTime}
                      {dayOffset > 0 ? (
                        <span className="font-bold text-stone-700 dark:text-slate-200">+{dayOffset}</span>
                      ) : null}
                    </>
                  ) : null}
                </p>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 z-10 p-1.5 rounded-lg text-stone-500 hover:text-stone-800 hover:bg-white/80 dark:text-slate-400 dark:hover:text-white dark:hover:bg-slate-800"
            aria-label={t.close}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-4 space-y-5">
          <div className="grid gap-3 grid-cols-2">
            <div className="rounded-xl border border-stone-200/80 dark:border-slate-700 bg-white/80 dark:bg-slate-800/60 p-3.5">
              <p className="text-[10px] uppercase tracking-wider text-stone-400 mb-1">{t.trainCarrier}</p>
              <p className="text-sm font-semibold text-stone-900 dark:text-white leading-snug [overflow-wrap:anywhere]">
                {softBreakAfterHyphens(
                  carrier ||
                    (isTrain
                      ? '—'
                      : lang.startsWith('ru')
                        ? 'Автобус'
                        : 'Bus'),
                )}
              </p>
              {isTrain && trainNumber && (
                <p className="text-xs text-stone-500 dark:text-slate-400 mt-1 tabular-nums">
                  {lang.startsWith('ru') ? '№' : '#'} {trainNumber}
                </p>
              )}
            </div>
            <div className="rounded-xl border border-stone-200/80 dark:border-slate-700 bg-white/80 dark:bg-slate-800/60 p-3.5">
              <p className="text-[10px] uppercase tracking-wider text-stone-400 mb-1">{t.tripInfo}</p>
              <p className="text-sm font-semibold text-stone-900 dark:text-white tabular-nums">
                {[
                  trip.distance != null && trip.distance > 0 ? formatDistance(trip.distance, settings.distanceUnit) : null,
                  durationLabel || null,
                ]
                  .filter(Boolean)
                  .join(' · ') || '—'}
              </p>
              {trip.price != null && trip.price > 0 && (
                <p className={`text-sm font-bold mt-1 tabular-nums ${theme.price}`}>
                  {formatNumber(trip.price)} {settings.currency ?? '₽'}
                </p>
              )}
            </div>
          </div>

          {isTrain && (
            <div className="rounded-xl border border-indigo-200/70 dark:border-indigo-800/50 bg-indigo-50/50 dark:bg-indigo-950/30 p-3.5">
              <p className="text-[10px] uppercase tracking-wider text-indigo-500/80 dark:text-indigo-300/70 mb-1">
                {t.trainBrandedName}
              </p>
              <p className="text-base font-semibold text-indigo-900 dark:text-indigo-100 leading-snug [overflow-wrap:anywhere]">
                {typeLabel
                  ? softBreakAfterHyphens(typeLabel)
                  : nameLoading
                    ? lang.startsWith('ru')
                      ? 'Определяем…'
                      : 'Looking up…'
                    : '—'}
              </p>
            </div>
          )}

          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-stone-500 dark:text-slate-400 mb-3">
              {isTrain ? t.trainStops : t.route}
            </h3>
            {isTrain && stopsLoading && (
              <div className="flex items-center gap-2 text-sm text-stone-500 dark:text-slate-400 mb-3">
                <Loader2 className="h-4 w-4 animate-spin" />
                {lang.startsWith('ru') ? 'Загружаем остановки…' : 'Loading stops…'}
              </div>
            )}
            <div className="relative pl-1">
              {timeline.map((city, i) => {
                const isFirst = i === 0;
                const isLast = i === timeline.length - 1;
                const isStop = !isFirst && !isLast;
                return (
                  <div key={`${city}-${i}`} className="relative flex gap-3 pb-5 last:pb-0">
                    {!isLast && (
                      <span
                        className={`absolute left-[0.55rem] top-4 bottom-0 w-px ${
                          isTrain ? 'bg-indigo-300 dark:bg-indigo-700' : 'bg-violet-300 dark:bg-violet-700'
                        }`}
                      />
                    )}
                    <span
                      className={`relative z-10 mt-1 h-2.5 w-2.5 shrink-0 rounded-full ring-4 ring-white dark:ring-slate-900 ${
                        isFirst
                          ? theme.routeDotFrom
                          : isLast
                            ? theme.routeDotTo
                            : 'bg-stone-400 dark:bg-slate-500'
                      }`}
                    />
                    <div className="min-w-0 pt-0">
                      <p className="text-[10px] uppercase tracking-wider text-stone-400">
                        {isFirst
                          ? t.originStation
                          : isLast
                            ? t.destinationStation
                            : lang.startsWith('ru')
                              ? `Остановка ${i}`
                              : `Stop ${i}`}
                      </p>
                      <p
                        className={`text-base leading-snug [overflow-wrap:anywhere] ${
                          isStop
                            ? 'font-medium text-stone-700 dark:text-slate-200'
                            : 'font-semibold text-stone-900 dark:text-white'
                        }`}
                      >
                        {softBreakAfterHyphens(city)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            {isTrain && !stopsLoading && routeReady && stops.length === 0 && (
              <p className="mt-2 text-xs text-stone-500 dark:text-slate-400">{t.noTrainStops}</p>
            )}
          </section>

          {!isTrain && trip.notes?.trim() && (
            <section className="rounded-xl border border-stone-200/80 dark:border-slate-700 bg-white/70 dark:bg-slate-800/50 p-3.5">
              <p className="text-[10px] uppercase tracking-wider text-stone-400 mb-1">{t.notes || 'Notes'}</p>
              <p className="text-sm text-stone-700 dark:text-slate-200 whitespace-pre-wrap leading-relaxed">{trip.notes}</p>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};
